import type {CSS} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

export type CalloutType =
  | 'info'
  | 'negative'
  | 'positive'
  | 'promotion'
  | 'private';

const Callout_ = styled(
  'div',
  (
    {$hasAction, $type}: {$hasAction: boolean; $type: CalloutType},
    designColors,
  ) => ({
    alignItems: 'center',
    backgroundColor:
      $type === 'info'
        ? designColors.badgeBlueBackground
        : $type === 'negative'
          ? designColors.badgeRedBackground
          : $type === 'positive'
            ? designColors.badgeGreenBackground
            : $type === 'promotion'
              ? designColors.promotionBackground
              : designColors.badgeGrayBackground,
    borderRadius: '6px',
    borderColor: 'currentColor',
    // $type === 'info'
    //   ? designColors.borderBlueNormal
    //   : $type === 'negative'
    //   ? designColors.borderRedAccent
    //   : designColors.borderGreenNormal,
    borderWidth: 1,
    borderStyle: 'solid',
    boxShadow: '0 1px 0 rgba(0, 0, 0, 0.15)',
    color:
      $type === 'info'
        ? designColors.badgeBlueText
        : $type === 'negative'
          ? designColors.badgeRedText
          : $type === 'positive'
            ? designColors.badgeGreenText
            : $type === 'promotion'
              ? designColors.promotionText
              : designColors.badgeGrayText,
    display: 'flex',
    fontWeight: 500,
    justifyContent: $hasAction ? 'space-between' : undefined,
    marginBottom: 12,
    padding: 12,
    // An action that does not fit beside a readable line of text (see
    // `ActionWrap`) goes below it, instead of squeezing the text into a column
    // of one word per line or running out of the callout.
    rowGap: 8,
    flexWrap: 'wrap',
    // A link in a callout is in the callout's text color, which is tested on
    // its fill, and is told apart by its underline. `headingBlue` is a color
    // for the page and cards: in dark mode it is under 4.5:1 on every fill
    // here. A bare `<a>` took the page's global link color, which also fails.
    // A `Button` with an `href` has a class, and keeps its own colors.
    ':not(:empty) .Link': {
      color: 'inherit',
    },
    ':not(:empty) a:not([class])': {
      color: 'inherit',
    },
  }),
  {className: 'Callout'},
);

// The text asks for 160px, and the action wraps below it when that and the
// action do not fit in a row. In a row, the text takes the room that is left.
const ActionWrap = styled(
  'div',
  {flex: '1 1 160px', marginRight: 12},
  {className: 'ActionWrapper'},
);

const Callout = ({
  action,
  children,
  id,
  style,
  type,
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
  id?: string;
  style?: CSS;
  type: CalloutType;
}) => (
  <Callout_
    $hasAction={action != null}
    $type={type}
    id={id}
    style={style}
    role={type !== 'info' ? 'alert' : undefined}
  >
    {action ? (
      <>
        <ActionWrap>{children}</ActionWrap>
        {action}
      </>
    ) : (
      <div className="ActionWrapper">{children}</div>
    )}
  </Callout_>
);

export default Callout;
